'use client';

/**
 * ProductSourcingIntel - Product-level sourcing intelligence
 *
 * Shows sourcing information for a selected product:
 * - Current pricing across vendors
 * - Availability status
 * - Alternative products (future)
 *
 * @component_role Product-level Insights
 * @business_rule Shows sourcing history for a specific product: which vendors
 *   carry it, historical prices across RFQs, lead time patterns, and availability
 *   notes. Not AI-synthesized (products are too granular for synthesis value).
 * @user_outcome Quick sourcing reference - see if this product has been quoted
 *   before and at what prices, without digging through old RFQs.
 * @integration_point klyde_edge /neo/graph/explore/product
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  Loader2,
  XCircle,
  Package,
  DollarSign,
  Factory,
  Clock,
  Box,
  TrendingDown,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useProductExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface ProductSourcingIntelProps {
  productId: string;
}

export function ProductSourcingIntel({ productId }: ProductSourcingIntelProps) {
  const productExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'product_explore'));
  const { data, isLoading, error } = useProductExplore(productExploreStrategy!, { product_id: productId });

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">Loading sourcing data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-destructive">
        <XCircle className="h-8 w-8 mb-2" />
        <p className="text-sm">Failed to load product data</p>
        <p className="text-xs mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  const product = data?.product;

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
        <Package className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Product not found</p>
      </div>
    );
  }

  const formatCurrency = (value: number | null) => {
    if (value === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-4 p-3">
      {/* Header */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">SOURCING INTELLIGENCE</h4>
        <div className="p-3 rounded-lg bg-card border border-border">
          <div className="font-medium">{product.name}</div>
          {product.sku && (
            <div className="text-xs text-muted-foreground mt-1">SKU: {product.sku}</div>
          )}
        </div>
      </div>

      {/* Pricing Summary */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">PRICING</h4>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 rounded-lg bg-card border border-border text-center">
            <DollarSign className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <div className="text-lg font-semibold text-success">
              {formatCurrency(product.list_price)}
            </div>
            <div className="text-xs text-muted-foreground">List Price</div>
          </div>
          <div className="p-3 rounded-lg bg-card border border-border text-center">
            <Box className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
            <div className={cn(
              "text-lg font-semibold",
              (product.qty_available || 0) > 0 ? "text-success" : "text-destructive"
            )}>
              {product.qty_available ?? 0}
            </div>
            <div className="text-xs text-muted-foreground">In Stock</div>
          </div>
        </div>
      </div>

      {/* Vendor Info */}
      {product.vendor_name && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">SUPPLIER</h4>
          <div className="p-3 rounded-lg bg-accent/50 border border-border">
            <div className="flex items-center gap-2">
              <Factory className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{product.vendor_name}</span>
            </div>
            {product.collection_name && (
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <Package className="h-3 w-3" />
                <span>{product.collection_name}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lead Time */}
      {product.lead_time && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">LEAD TIME</h4>
          <div className="flex items-center gap-2 p-2 rounded bg-info/10 border border-info/20">
            <Clock className="h-4 w-4 text-info" />
            <span className="text-sm">{product.lead_time}</span>
          </div>
        </div>
      )}

      {/* Dimensions */}
      {(product.width_mm || product.height_mm || product.depth_mm) && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">DIMENSIONS</h4>
          <div className="grid grid-cols-3 gap-2 text-center">
            {product.width_mm && (
              <div className="p-2 rounded bg-muted/50">
                <div className="text-sm font-medium">{product.width_mm}</div>
                <div className="text-xs text-muted-foreground">W (mm)</div>
              </div>
            )}
            {product.depth_mm && (
              <div className="p-2 rounded bg-muted/50">
                <div className="text-sm font-medium">{product.depth_mm}</div>
                <div className="text-xs text-muted-foreground">D (mm)</div>
              </div>
            )}
            {product.height_mm && (
              <div className="p-2 rounded bg-muted/50">
                <div className="text-sm font-medium">{product.height_mm}</div>
                <div className="text-xs text-muted-foreground">H (mm)</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Future: Alternatives Section */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">ALTERNATIVES</h4>
        <div className="p-3 rounded-lg bg-muted/30 border border-dashed border-border text-center">
          <TrendingDown className="h-6 w-6 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-xs text-muted-foreground">
            Alternative product suggestions coming soon
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-xs text-muted-foreground pt-2 border-t border-border">
        Data from Neo4j knowledge graph
      </div>
    </div>
  );
}
